import CalendarDateList from "calendar-date-list";
import "./booking.css";
import moment from "moment-timezone";
import "moment/locale/th";
import { useAuth } from "../hooks/use-auth";
import { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCaretRight,
  faCaretLeft,
  faCircleXmark,
  faAdd,
  faEdit,
  faUsers,
  faXmark,
  faXmarkSquare,
} from "@fortawesome/free-solid-svg-icons";
import CallAPI from "../utils/call-api";

moment.locale("th");

export default function Booking() {
  const getDateRange = () => {
    const now = moment();
    const calendar = new CalendarDateList(1);
    const weeks = calendar.getDates(now.year(), now.month());
    let week = weeks.find((dates) => dates[dates.length - 1].date > now);
    if (!week) week = weeks[weeks.length - 1];
    const dateFormat = "YYYY-MM-DD";
    let dates = week.map((d) => moment(d.date).add(dateCursor, "d"));
    return dates.map((d) => d.format(dateFormat));
  };

  const [editingClassSession, setEditingClassSession] = useState();
  const [isEdit, setIsEdit] = useState(false);
  const [dateCursor, setDateCursor] = useState(0);
  const dates = getDateRange();
  const start = dates[0];
  const end = dates[dates.length - 1];
  const [loading, setLoading] = useState(false);
  const [classSessions, setClassSessions] = useState({});
  const [showUserList, setShowUserList] = useState(false);
  const [currentClassDay, setCurrentClassDay] = useState();

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      setClassSessions(getInitClassSessions(dates));
      const sessions = await getClassSessions({ start, end });
      setClassSessions((prev) => ({ ...Object.assign(prev, sessions) }));
      setLoading(false);
    };
    getData();
  }, [start, end]);

  const auth = useAuth();

  useEffect(() => {
    auth.refresh();
  }, []);

  const handleChange = (evt) => {
    let { name, value, type, checked } = evt.target;
    if (type === "checkbox") {
      value = checked;
    }
    if (type === "time") {
      name = "datetime";
      const [h, m] = value.split(":");
      value = moment(editingClassSession.datetime)
        .hour(h)
        .minute(m)
        .format("YYYY-MM-DD HH:mm");
    }
    setEditingClassSession((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (evt) => {
    evt.preventDefault();
    const { _id, users, __v, ...data } = editingClassSession;
    const res = _id
      ? await CallAPI.put(`/class-session/${_id}`, data)
      : await CallAPI.post("/class-session", data);
    const session = res.data;
    setClassSessions((prev) => {
      const key = moment(session.datetime).format("YYYYMMDD");
      const i = prev[key].findIndex((s) => s._id === session._id);
      if (i === -1) {
        prev[key].push(session);
      } else {
        prev[key].splice(i, 1, session);
      }
      return { ...prev };
    });
    setIsEdit(false);
  };

  const handleAddClick = (date) => {
    return (evt) => {
      setEditingClassSession({
        datetime: moment(date).format("YYYY-MM-DD HH:mm"),
        name: "",
        maxUsers: 3,
      });
      setIsEdit(true);
    };
  };

  const handleEditClick = (session) => {
    return (evt) => {
      setEditingClassSession(session);
      setIsEdit(true);
    };
  };

  const handleSelectClick = (session) => {
    return async (evt) => {
      const hours = moment().diff(session.datetime, "h");
      if (
        hours > -12 &&
        !window.confirm(
          `คลาสจะเริ่มในอีก ${Math.abs(
            hours
          )} ชั่วโมง\nเมื่อจองแล้วจะไม่สามารถยกเลิกได้`
        )
      ) {
        return;
      }
      const data = { userid: auth.user._id };
      const isInSession =
        session.users.findIndex((s) => s._id === auth.user._id) !== -1;
      const func = isInSession ? "remove" : "add";
      const numSessions = isInSession
        ? auth.user.numSessions + 1
        : auth.user.numSessions - 1;
      const res = await CallAPI.put(
        `/class-session/${session._id}/user/${func}`,
        data
      );
      setClassSessions((prev) => {
        const key = moment(res.data.datetime).format("YYYYMMDD");
        const i = prev[key].findIndex((s) => s._id === res.data._id);
        if (i > -1) prev[key].splice(i, 1, res.data);
        return { ...prev };
      });
      auth.setNumSessions(numSessions);
    };
  };

  const handleUserListClick = (date) => {
    return (evt) => {
      setCurrentClassDay(classSessions[date]);
      setShowUserList(true);
    };
  };

  const handleDeleteClick = (session) => {
    return async (evt) => {
      if (
        session.users.length > 0 &&
        !window.confirm(
          `ต้องการยกเลิกคลาส\n${moment(session.datetime).format(
            "DD/MM/YY HH:mm"
          )}\n${session.name}\nจำนวนนักเรียน ${session.users.length} คน`
        )
      ) {
        return;
      }
      await CallAPI.delete(`/class-session/${session._id}`);
      setClassSessions((prev) => {
        const key = moment(session.datetime).format("YYYYMMDD");
        const i = prev[key].findIndex((s) => s._id === session._id);
        if (i > -1) prev[key].splice(i, 1);
        return { ...prev };
      });
    };
  };

  const getClassSessions = async (params) => {
    const res = await CallAPI.get("/class-session", params);
    const tmp = {};
    for (const session of res.data) {
      const key = moment(session.datetime).format("YYYYMMDD");
      tmp[key] = tmp[key] || [];
      tmp[key].push(session);
    }
    return tmp;
  };

  const getInitClassSessions = (dates) => {
    const tmp = {};
    for (const date of dates) {
      tmp[moment(date).format("YYYYMMDD")] = [];
    }
    return tmp;
  };

  const handlePrevClick = (evt) => {
    setDateCursor((prev) => prev - 7);
  };

  const handleNextClick = (evt) => {
    setDateCursor((prev) => prev + 7);
  };

  return (
    <>
      {!auth.user.isAdmin ? (
        <div className="num-sessions">จำนวนคลาส: {auth.user.numSessions}</div>
      ) : (
        ""
      )}
      <div className="control">
        <div>
          <button
            onClick={handlePrevClick}
            disabled={!auth.user.isAdmin && dateCursor === 0}
          >
            <FontAwesomeIcon icon={faCaretLeft} />
          </button>
        </div>
        <h2>ตารางจองคลาสพิลาทิส</h2>
        <div>
          <button
            onClick={handleNextClick}
            disabled={!auth.user.isAdmin && dateCursor === 7}
          >
            <FontAwesomeIcon icon={faCaretRight} />
          </button>
        </div>
      </div>

      {loading ? (
        <div>loading</div>
      ) : (
        <>
          {Object.keys(classSessions).map((date) => (
            <ClassDay
              key={date}
              date={date}
              sessions={classSessions[date]}
              onAddClick={handleAddClick}
              onSelectClick={handleSelectClick}
              onEditClick={handleEditClick}
              onUserListClick={handleUserListClick}
              onDeleteClick={handleDeleteClick}
            />
          ))}
        </>
      )}

      {isEdit && (
        <div className="modal modal-class-session">
          <div className="container">
            <div className="header">
              <button
                className="as-a btn-close"
                onClick={() => setIsEdit(false)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="content">
              <form onSubmit={handleSubmit}>
                <div>
                  <input
                    type="time"
                    name="time"
                    placeholder="เวลา"
                    defaultValue={moment(editingClassSession.datetime).format(
                      "HH:mm"
                    )}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <input
                    type="name"
                    name="name"
                    placeholder="ชื่อ"
                    value={editingClassSession.name}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <input
                    type="number"
                    name="maxUsers"
                    placeholder="จำนวนนักเรียนสูงสุด"
                    value={editingClassSession.maxUsers}
                    onChange={handleChange}
                  />
                </div>
                <div>
                  <button type="submit">บันทึก</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {showUserList && (
        <div className="modal modal-user-list">
          <div className="container">
            <div className="header">
              <button
                className="as-a btn-close"
                onClick={() => setShowUserList(false)}
              >
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="content">
              {currentClassDay.map((session) => (
                <div className="session-detail">
                  <div className="time">
                    {moment(session.datetime).format("HH:mm")}
                  </div>
                  <div className="name">{session.name}</div>
                  <ol>
                    {session.users.map((u) => (
                      <li>{u.name}</li>
                    ))}
                  </ol>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ClassDay({
  date,
  sessions,
  onEditClick,
  onSelectClick,
  onAddClick,
  onUserListClick,
  onDeleteClick,
}) {
  const auth = useAuth();
  const hasUsers = sessions.findIndex((s) => s.users.length > 0) !== -1;
  const now = moment();

  return (
    <div className="class-day">
      <div className="date" colSpan="4">
        {moment(date).format("dddd DD/MM/YY")}
        {auth.user.isAdmin && (
          <button
            className="as-a"
            onClick={onUserListClick(date)}
            disabled={!hasUsers}
          >
            <FontAwesomeIcon icon={faUsers} />
          </button>
        )}
      </div>
      <div className="list-class-session">
        {sessions
          .sort((a, b) => moment(a.datetime).diff(b.datetime))
          .map((session) => (
            <div className="class-session" key={session._id}>
              <div className="time">
                {moment(session.datetime).format("HH:mm")}
              </div>
              <div>{session.name}</div>
              <div className="numUsers">{`${session.users.length} / ${session.maxUsers}`}</div>
              {auth.user.isAdmin ? (
                <>
                  {now < moment(session.datetime) && (
                    <div className="list-btn">
                      <button className="as-a" onClick={onEditClick(session)}>
                        <FontAwesomeIcon icon={faEdit} />
                      </button>
                      <button className="as-a" onClick={onDeleteClick(session)}>
                        <FontAwesomeIcon icon={faXmarkSquare} />
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <SelectButton
                  session={session}
                  onSelectClick={onSelectClick(session)}
                />
              )}
            </div>
          ))}
        {auth.user.isAdmin && now < moment(date).add(1, "d") && (
          <div className="class-session">
            <button className="btn-add" onClick={onAddClick(date)}>
              <FontAwesomeIcon icon={faAdd} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SelectButton({ session, onSelectClick }) {
  const auth = useAuth();
  const isInSession =
    session.users.findIndex((s) => s._id === auth.user._id) !== -1;
  let text = isInSession ? "จอง" : "ว่าง";
  let className = isInSession ? "selected" : "";
  const now = moment();
  const expired = now > moment(session.datetime);
  const cancelable = now < moment(session.datetime).subtract(12, "h");
  const inActiveDuration = moment(session.datetime).isBetween(
    auth.user.startDate,
    moment(auth.user.endDate).add(1, "d")
  );
  let disabled = expired || (isInSession && !cancelable) || !inActiveDuration;
  const isFull = !isInSession && session.users.length === session.maxUsers;
  if (expired) {
    text = <FontAwesomeIcon icon={faCircleXmark} />;
    className = "full";
  }
  text = isFull ? "เต็ม" : text;

  return (
    <button
      className={`btn-select ${className}`}
      onClick={onSelectClick}
      disabled={disabled}
    >
      {text}
    </button>
  );
}
